import React from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { signInWithGoogle } from '../services/auth';
import { MessageSquare, Users, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
  } from "./ui/accordion";

  interface LandingPageProps {
    user?: User | null; 
    onError?: (error: string) => void; 
  }

interface ExampleDecision {
    question: string;
    context: string;
    synthesis: string;
  }


const exampleDecisions: ExampleDecision[] = [
  {
    question: "Which features should we prioritize for our Q3 product release?",
    context: "30-person cross-functional team with engineering, design, sales, and customer support",
    synthesis: "There's overwhelming support for focusing on mobile experience improvements, with 80% of team members ranking it as a top priority. Search functionality enhancements received broad but less urgent support. While the engineering team emphasized backend performance concerns, customer-facing teams strongly advocated for UI/UX improvements. We recommend prioritizing mobile experience first, with a smaller team beginning exploratory work on search improvements."
  },

  {
    question: "What features should our neighborhood community garden include?",
    context: "25 neighbors contributing to design and funding of shared space",
    synthesis: "Almost everyone agreed that water access and raised beds should be the highest priorities. There's significant interest in educational components, with many supporting workshops and several suggesting children's programming. A shared tool shed received strong support from 20 participants, though opinions differed on the maintenance approach. We recommend allocating about 60% of the budget to infrastructure, with the remainder split between educational programming and ongoing maintenance."
  },
  {
    question: "How should we structure our hybrid work policy?",
    context: "45-person company transitioning from remote-first to hybrid model",
    synthesis: "The vast majority of our team prefers a flexible approach rather than mandated office days. The most supported model combines team-determined in-office days with flexible remote work. 15 people specifically raised concerns about meeting scheduling and collaborative work. We recommend implementing core hours (10am-3pm) on designated team days with flexibility on location for non-collaborative work."
  },
  {
    question: "What should be the funding priorities for our city's discretionary budget?",
    context: "65 neighborhood representatives providing input on $2M discretionary funds",
    synthesis: "Infrastructure improvements, particularly road repairs, emerged as the clear top priority among participants. Parks and recreation enhancements were the second most mentioned priority, with representatives from neighborhoods lacking green space expressing the strongest preference. While business district improvements had passionate advocates, it was ranked in the top three by only 29 representatives. Based on this input, we recommend allocating nearly half the budget to infrastructure, about a third to parks, and dividing the remainder between business district support and community programming."
  },
  {
    question: "How should we update our community moderation guidelines?",
    context: "1,200+ members of a growing online community platform",
    synthesis: "Most members support stronger automated moderation of harmful content, while maintaining human review processes. There's broad consensus that political content should be contained to designated spaces rather than prohibited entirely. Regarding account suspensions, 65% favor a graduated warning system. Notably, long-time members showed greater concern about over-moderation compared to newer members. We recommend implementing opt-out content filtering plus human-reviewed designated spaces for sensitive topics."
  },
  {
    question: "What topics should our industry conference prioritize next year?",
    context: "850 potential attendees from across the industry providing input",
    synthesis: "AI implementation received overwhelming support across all industry segments, with 82% of respondents ranking it in their top three topics. Sustainability practices ranked second overall but showed significant variation by industry sector, with manufacturing professionals showing much stronger interest than those in software. Supply chain resilience topics were emphasized by representatives from midsize companies but weren't as highly prioritized by enterprise participants. Based on this feedback, we recommend dedicating several keynote tracks to AI applications, with additional focus on sustainability and specialized workshops for sector-specific concerns."
  }
];

export function LandingPage({ user, onError }: LandingPageProps) {

  const navigate = useNavigate();

  const handleGetStarted = async () => {
    if (!user) {
      try {
        await signInWithGoogle();
        navigate('/create');
      } catch (error) {
        if (onError) { 
          onError('Failed to sign in with Google. Please try again.');
        }
      }
    } else {
      navigate('/create');
    }
  };

  const location = useLocation();

    useEffect(() => {
        if (location.hash) {
        setTimeout(() => {
            const element = document.querySelector(location.hash);
            if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
        }
    }, [location]);


  return (
    <div className="flex flex-col gap-24">
      {/* Hero Section */}
      <div className="flex items-center min-h-[50vh] text-center relative bg-white">
        <div className="absolute inset-0 bg-gradient-radial from-primary/20 via-secondary/10 to-transparent"></div>
        <div className="w-full">
          <div className="relative max-w-4xl mx-auto space-y-8 w-full py-12 px-4 md:px-8">
            <h1 className="text-6xl font-bold tracking-tight">
              Take the pain out of group decisions.
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Invite your community to share ideas, information, and opinions with our chatbot. WhatSayUs analyzes and summarizes the group's perspective.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center max-w-sm mx-auto md:max-w-none">
              <Button 
                size="lg" 
                className="text-lg px-8 py-3 h-auto shadow-md hover:shadow-lg hover:shadow-secondary/20 transition-shadow"
                onClick={handleGetStarted}
              >
                Make a group decision
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="text-lg px-8 py-3 h-auto shadow-md hover:shadow-lg hover:shadow-secondary/20 transition-shadow"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                How it works
              </Button>
            </div>
          </div>
        </div>
    </div>

      {/* How It Works Section */}
      <div id="how-it-works" className="text-center space-y-12 px-4 md:px-8">
        <h2 className="text-3xl font-semibold">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          
          <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
              <MessageSquare className="h-8 w-8 text-primary" />
              </div>
            <h3 className="text-xl font-medium">Ask Your Question</h3>
            <p className="text-muted-foreground">
              Create a decision space with your question and any important context or constraints.
            </p>
          </div>

          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-xl relative flex items-center justify-center">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-medium">Share with Your Group</h3>
            <p className="text-muted-foreground">
              Invite participants to share their thoughts privately with our AI assistant.
            </p>
          </div>

          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-xl relative flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-medium">Get the Group's View</h3>
            <p className="text-muted-foreground">
              We synthesize everyone's input into clear insights and next steps.
            </p>
          </div>

        </div>
      </div>
      
      {/* Examples Section */}
      <div id="examples" className="space-y-12 px-4 md:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-semibold mb-4">Example Decisions</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            See how groups use WhatSayUs to make better decisions together
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {exampleDecisions.map((example, index) => (
            <Card key={index} className="flex flex-col hover:shadow-lg hover:shadow-secondary/20 transition-shadow">
              <CardHeader className="border-l-4 border-secondary">
                <CardTitle className="text-xl">{example.question}</CardTitle>
                <p className="text-sm text-muted-foreground">{example.context}</p>
              </CardHeader>
              <CardContent className="flex-grow">
                <div className="prose prose-sm">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Group's Perspective
                  </h4>
                  <p>{example.synthesis}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div id="faq" className="space-y-8 pb-16">
          <div className="text-center">
            <h2 className="text-3xl font-semibold mb-4">Frequently Asked Questions</h2>
          </div>
          
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <Accordion type="single" collapsible className="space-y-4 text-left">
              <AccordionItem value="why">
                <AccordionTrigger className="text-xl text-left">
                  Why not just use Zoom, Slack, email, etc.?
                </AccordionTrigger>
                <AccordionContent className="text-lg text-muted-foreground">
                  Group discussions in chat, video, email or in person tend to be dominated by the loudest voices, and it's hard to develop consensus as the number of participants grows. WhatSayUs lets everyone share their full perspective privately, then synthesizes insights that might get missed in typical group discussions. It distills signal from noise.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="surveys">
                <AccordionTrigger className="text-xl text-left">
                  What makes WhatSayUs different from using a survey?
                </AccordionTrigger>
                <AccordionContent className="text-lg text-muted-foreground">
                  Unlike surveys with rigid questions and limited responses, WhatSayUs offers conversational interactions where participants can explain nuances, ask clarifying questions, and think through their perspectives. The AI assistant can probe deeper into responses, uncovering insights that static surveys miss. Additionally, our synthesis goes beyond data aggregation to identify patterns, consensus points, and potential compromises, delivering actionable recommendations rather than just raw data.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="who">
                <AccordionTrigger className="text-xl text-left">
                  Who is WhatSayUs designed for?
                </AccordionTrigger>
                <AccordionContent className="text-lg text-muted-foreground">
                  WhatSayUs is meant to enable groups of all shapes and sizes to make decisions together, especially when working remotely or asynchronously. It's ideal for team leaders, community organizers, and anyone coordinating group decisions where it's important that everyone's voice is heard. It's not designed for simple yes/no decisions or very small groups.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="size">
                <AccordionTrigger className="text-xl text-left">
                  Is there a limit on how many people can participate in making a decision?
                </AccordionTrigger>
                <AccordionContent className="text-lg text-muted-foreground">
                  WhatSayUs is built to handle any number of participants. If you expect thousands of people will participate, reach out to us at hello@whatsayus.com so we can best support you. If your group is just a few people, it may be easiest to just have a quick conversation on a video call or chat.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="privacy">
                <AccordionTrigger className="text-xl text-left">
                  Who can view and participate in my group's decision?
                </AccordionTrigger>
                <AccordionContent className="text-lg text-muted-foreground">
                  Only people with your decision link can access the discussion. Each participant's conversation with the AI is private - other participants can only see the final synthesized response, but not individual inputs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="neutrality">
                <AccordionTrigger className="text-xl text-left">
                  Can my group rely on WhatSayUs as a neutral party, or do you influence decisions?
                </AccordionTrigger>
                <AccordionContent className="text-lg text-muted-foreground">
                  We built our AI chatbot to weight every participant's views equally. It looks for consensus and majority opinions while acknowledging minority viewpoints. That said, WhatSayUs relies on some of the largest and most sophisticated language models, which may have biases that are outside our control.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="cost">
                <AccordionTrigger className="text-xl text-left">
                  How much does WhatSayUs cost?
                </AccordionTrigger>
                <AccordionContent className="text-lg text-muted-foreground">
                  WhatSayUs is completely free to use during our beta period. Each decision space includes unlimited participants and responses. We plan to add optional paid features with time, like the option to use different AI models  — please reach out to us at hello@whatsayus.com to share your ideas for what we should build next!
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </div>
        </div>

        <div className="text-center px-4 md:px-8">
          <Button 
            size="lg"
            className="text-lg px-8 py-3 h-auto"
            onClick={handleGetStarted}
          >
            Launch Your Decision
          </Button>
        </div>
      </div>
    </div>
  );
}